import React from 'react';

import {
  DeviceDetailsType,
  usePaginatedDeviceCommands,
} from '@portals/api/organizations';
import { BadgeCell, PaginatedTable } from '@portals/table';
import {
  CommandStatusType,
  CommandType,
  PaginatedFilterTypeEnum,
  TableColumn,
} from '@portals/types';
import { getCommandStatusInfo, prettyTime } from '@portals/utils';

import { CommandDetailsPanel } from './CommandDetailsPanel';

interface AllCommandsProps {
  device: DeviceDetailsType;
}

const COLUMNS: TableColumn<CommandType>[] = [
  {
    dataField: 'friendly_name',
    text: 'Name',
    sort: true,
    minWidth: 300,
    filter: {
      type: PaginatedFilterTypeEnum.Text,
    },
  },
  {
    dataField: 'status',
    text: 'Status',
    sort: true,
    filter: {
      type: PaginatedFilterTypeEnum.Select,
      options: {
        [CommandStatusType.Pending]: getCommandStatusInfo(
          CommandStatusType.Pending
        ).label,
        [CommandStatusType.InProgress]: getCommandStatusInfo(
          CommandStatusType.InProgress
        ).label,
        [CommandStatusType.Done]: getCommandStatusInfo(CommandStatusType.Done)
          .label,
        [CommandStatusType.Failed]: getCommandStatusInfo(
          CommandStatusType.Failed
        ).label,
        [CommandStatusType.Aborted]: getCommandStatusInfo(
          CommandStatusType.Aborted
        ).label,
      },
    },
    formatter: (_, { status }) => {
      const { color, label } = getCommandStatusInfo(status);
      return <BadgeCell color={color} label={label} />;
    },
  },
  {
    dataField: 'created_at',
    text: 'Created',
    sort: true,
    filter: {
      type: PaginatedFilterTypeEnum.Date,
    },
    formatter: (_, { created_at }) => prettyTime(created_at),
  },
  {
    dataField: 'message',
    text: 'Message',
    sort: true,
    filter: {
      type: PaginatedFilterTypeEnum.Text,
    },
    minWidth: 400,
  },
];

export function AllCommands({ device }: AllCommandsProps) {
  return (
    <PaginatedTable<CommandType>
      keyField="id"
      name="organizations.overview.device.commands"
      dataHook={usePaginatedDeviceCommands}
      dataHookUrl={`ui/organization/devices/${device.id}/commands`}
      columns={COLUMNS}
      noHeader
      isUrlSyncEnabled={false}
      defaultSortBy={[{ id: 'created_at', desc: true }]}
      noDataIndication={{ title: 'No commands' }}
      detailsPanel={{
        type: 'inner',
        renderer: ({ row, onClose }) => (
          <CommandDetailsPanel
            command={row.original}
            device={device}
            onClose={onClose}
          />
        ),
      }}
    />
  );
}
